import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/layout/DashboardLayout";

import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import FindingsPage from "./pages/FindingsPage";
import TasksPage from "./pages/TasksPage";
import NotificationsPage from "./pages/NotificationsPage";
import FrameworksPage from "./pages/FrameworksPage";
import FrameworkDetailPage from "./pages/FrameworkDetailPage";
import ControlsPage from "./pages/ControlsPage";
import ControlDetailPage from "./pages/ControlDetailPage";
import AssessmentsPage from "./pages/AssessmentsPage";
import AssessmentDetailPage from "./pages/AssessmentDetailPage";
import EvidencePage from "./pages/EvidencePage";
import ComplianceDashboardPage from "./pages/ComplianceDashboardPage";
import AuditsPageV2 from "./pages/AuditsPageV2";
import AuditDetailPage from "./pages/AuditDetailPage";
import AuditCalendarPage from "./pages/AuditCalendarPage";
import RisksPageV2 from "./pages/RisksPageV2";
import RiskDetailPage from "./pages/RiskDetailPage";
import RiskMatrixPage from "./pages/RiskMatrixPage";
import RiskTrendsPage from "./pages/RiskTrendsPage";
import RemediationsPage from "./pages/RemediationsPage";
import RemediationDetailPage from "./pages/RemediationDetailPage";
import AdvisorPage from "./pages/AdvisorPage";
import ReportsPage from "./pages/ReportsPage";
import ActivityLogPage from "./pages/ActivityLogPage";

function Home() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center gap-3">
      <div className="text-6xl font-semibold text-primary">404</div>
      <div className="text-lg font-medium">Page not found</div>
      <a href="/dashboard" className="text-sm text-primary hover:underline">Back to dashboard</a>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/audits" element={<AuditsPageV2 />} />
              <Route path="/audits/:id" element={<AuditDetailPage />} />
              <Route path="/audit-calendar" element={<AuditCalendarPage />} />
              <Route path="/risks" element={<RisksPageV2 />} />
              <Route path="/risks/:id" element={<RiskDetailPage />} />
              <Route path="/risk-matrix" element={<RiskMatrixPage />} />
              <Route path="/risk-trends" element={<RiskTrendsPage />} />
              <Route path="/remediations" element={<RemediationsPage />} />
              <Route path="/remediations/:id" element={<RemediationDetailPage />} />
              <Route path="/advisor" element={<AdvisorPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/activity-log" element={<ActivityLogPage />} />
              <Route path="/findings" element={<FindingsPage />} />
              <Route path="/tasks" element={<TasksPage />} />

              {/* Compliance Management Module */}
              <Route path="/compliance-dashboard" element={<ComplianceDashboardPage />} />
              <Route path="/frameworks" element={<FrameworksPage />} />
              <Route path="/frameworks/:id" element={<FrameworkDetailPage />} />
              <Route path="/controls" element={<ControlsPage />} />
              <Route path="/controls/:id" element={<ControlDetailPage />} />
              <Route path="/assessments" element={<AssessmentsPage />} />
              <Route path="/assessments/:id" element={<AssessmentDetailPage />} />
              <Route path="/evidence" element={<EvidencePage />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute roles={["admin", "compliance_officer"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <OrganizationsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
